import type { AxiosError } from 'axios';
import type { ScopeCollisionDto } from '~/data/openapi-client/index';

/**
 * The API's error envelope. `ExceptionMiddleware` writes `{ message, reason, data }` - the machine-readable
 * code is in `reason`, not `code`, and `data` carries whatever detail makes the refusal actionable. The
 * generated SDK models no error type at all, so it is declared here.
 */
export type ApiErrorBody = {
  reason?: string;
  /** Not written by the current middleware; read as a fallback so a rename does not silently break this. */
  code?: string;
  message?: string;
  data?: unknown;
};

export const SCOPE_COLLISION_CODE = 'ScopeCollision';
export const COPY_ALREADY_RUNNING_CODE = 'CopyAlreadyRunning';
export const CROSS_COUNTRY_NEEDS_REASON_CODE = 'CrossCountryCopyNeedsReason';

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const errorBody = (error: unknown): ApiErrorBody | undefined => {
  const data = (error as AxiosError<ApiErrorBody> | undefined)?.response?.data;

  return isPlainRecord(data) ? (data as ApiErrorBody) : undefined;
};

/** The backend's error code, when the response body carries one. */
export function getApiErrorCode(error: unknown): string | undefined {
  const body = errorBody(error);

  if (typeof body?.reason === 'string') return body.reason;

  return typeof body?.code === 'string' ? body.code : undefined;
}

export function getApiErrorMessage(error: unknown): string | undefined {
  const message = errorBody(error)?.message;

  return typeof message === 'string' ? message : undefined;
}

/**
 * The clashing scopes a 409 `ScopeCollision` carries in `data`.
 *
 * Returns an empty list for any other error, and for a `ScopeCollision` whose payload is not the expected
 * shape. That is the safe direction: the caller uses this list to build the skip override, so "I could not
 * read the collisions" must never become "there are none to skip" - it leaves the start blocked instead.
 */
export function getScopeCollisions(error: unknown): ScopeCollisionDto[] {
  if (getApiErrorCode(error) !== SCOPE_COLLISION_CODE) return [];

  const { data } = errorBody(error) ?? {};
  const raw = Array.isArray(data) ? data : isPlainRecord(data) ? data.collisions : undefined;

  if (!Array.isArray(raw)) return [];

  return raw.filter(
    (collision): collision is ScopeCollisionDto =>
      isPlainRecord(collision) && typeof collision.sourceScopeId === 'string',
  );
}
